import './update_alt_text.scss';
import React from 'react';
import { useModal } from '../../../../components/Modal/PresentedModal';
import { t } from '../../../../locales/translate';
import { FormForm } from '../../../../components/Form/FormForm';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { graphql } from '../../../../generated';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { handleError } from '../../../../model/notification';
import { Stack } from '@nerdgeschoss/shimmer-component-stack';
import { Image } from '../../../../components/image/image';

const GET_ALT_TEXT = graphql(`
  query GetAltText($id: ID!) {
    asset(id: $id) {
      id
      altDe
      altEn
      alt
      ... on Image {
        url(width: 200)
      }
    }
  }
`);

const UPDATE_ALT_TEXT = graphql(`
  mutation UpdateAltText($id: ID!, $altDe: String, $altEn: String) {
    updateAsset(input: { id: $id, altDe: $altDe, altEn: $altEn }) {
      clientMutationId
      asset {
        id
        altDe
        altEn
      }
    }
  }
`);

interface Props {
  assetId: string;
  onUpdate?: () => void;
}

export function UpdateAltText({ assetId, onUpdate }: Props): JSX.Element {
  const { open, Modal, close } = useModal();
  const { data } = useSuspenseQuery(GET_ALT_TEXT, {
    variables: {
      id: assetId,
    },
  });
  const [update] = useMutation(UPDATE_ALT_TEXT);
  const { fields, onSubmit, dirty, valid, submissionStatus } = useForm<{
    en?: string;
    de?: string;
  }>({
    model: {
      en: data?.asset.altEn || undefined,
      de: data?.asset.altDe || undefined,
    },
    onSubmit: async ({ model }) => {
      await update({
        variables: {
          id: assetId,
          altEn: model.en,
          altDe: model.de,
        },
      });
      if (onUpdate) {
        onUpdate();
      }
      close();
    },
    onSubmitError: handleError,
  });

  return (
    <div className="update-alt-text">
      <Modal open={open} title={t.model.product.altText.update}>
        <FormForm
          onSubmit={onSubmit}
          onClose={close}
          canSubmit={dirty && valid}
          loading={submissionStatus === 'submitting'}
        >
          <Stack lineDesktop gap={32}>
            {data.asset.__typename === 'Image' && <Image src={data.asset.url} width={200} alt={data.asset.alt || ''} />}

            <div className="update-alt-text__fields">
              <Stack gap={8} align="start">
                <FormTextField {...fields.en} label={t.model.product.altText.english} />
                <FormTextField {...fields.de} label={t.model.product.altText.german} />
              </Stack>
            </div>
          </Stack>
        </FormForm>
      </Modal>
    </div>
  );
}
